import { matchPath } from 'react-router';

import { takeLatest, take, select, put, call, takeEvery, debounce, fork } from 'redux-saga/effects';

import * as navigateActions from 'actions/navigateActions';
import { restClient } from 'config/store/configureStore';
import * as allTypes from 'constants/ActionTypes';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { AUDIT } from 'constants/ExtensionConstants';
import { PATH } from 'constants/RoutesConstants';
import { DEALER_STATUS } from 'constants/StatusConstants';
import { fetchPage } from 'modules/app/sagas/resolveAll';
import { applicationIsReady } from 'modules/app/selectors';
import * as dashboardActions from 'modules/auditModels/actions/dashboard';
import * as unpublishModalActions from 'modules/auditModels/actions/modal/unpublish';
import { AUDIT_MODEL_STATUS } from 'modules/auditModels/constants';
import { getFields } from 'modules/auditModels/selectors/fields';
import { getById } from 'modules/auditModels/selectors/general';
import { getTranslations } from 'modules/auditModels/selectors/translations';
import * as dealerActions from 'modules/dealers/actions';
import * as groupActions from 'modules/groups/actions';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';
import { getDealerDefaultLanguage } from 'selectors/dealerSelectors';
import { getLanguages } from 'selectors/languageSelectors';
import IntlService from 'services/api/IntlService';

import auditModelsDashboardSagas from './dashboard';
import * as detailsActions from '../actions/details';
import * as duplicateActions from '../actions/duplicate';
import * as duplicateModalActions from '../actions/modal/duplicate';
import * as selectActions from '../actions/select';
import * as actionTypes from '../actionTypes';
import * as auditModelsSelectSelector from '../selectors/select';

function* startDetailsApplication({ payload }) {
  const { pathname } = payload.location;
  const isReady = yield select(applicationIsReady);

  if (!isReady) {
    yield take(allTypes.FETCH_DEALER_SUCCESS);
  }

  const matchDashboard = matchPath(PATH.AUDIT_MODELS.path, pathname);
  if (matchDashboard) {
    yield put(duplicateModalActions.hideModal());
  }

  const match = matchPath(PATH.AUDIT_MODELS_DETAILED.path, pathname);
  if (match) {
    const languages = yield select(getLanguages);
    const fields = yield select(getFields);
    const translations = yield select(getTranslations);
    const defaultLanguage = yield select(getDealerDefaultLanguage);

    const hasFields = Object.keys(fields).length > 0;
    const hasTranslations = Object.keys(translations).length > 0;

    const { auditModelId } = match.params;
    const isNew = auditModelId === NEW_ENTITY_ID;
    let idToSelect = null;
    let language = defaultLanguage;

    try {
      if (!hasFields) {
        yield put(detailsActions.fetchFields());
        const response = yield take([actionTypes.FETCH_FIELDS_SUCCESS, actionTypes.FETCH_FIELDS_FAILURE]);
        if (response.type === actionTypes.FETCH_FIELDS_FAILURE) {
          throw response.payload;
        }
      }

      if (!hasTranslations) {
        yield put(detailsActions.fetchTranslations(languages));
        const response = yield take([actionTypes.FETCH_TRANSLATIONS_SUCCESS, actionTypes.FETCH_TRANSLATIONS_FAILURE]);
        if (response.type === actionTypes.FETCH_TRANSLATIONS_FAILURE) {
          throw response.payload;
        }
      }

      if (!isNew) {
        yield put(detailsActions.fetchRequest(auditModelId));
        const response = yield take([actionTypes.FETCH_SUCCESS, actionTypes.FETCH_FAILURE]);
        if (response.type === actionTypes.FETCH_FAILURE) {
          throw response.payload;
        }
        const { auditModel } = response.payload;
        idToSelect = auditModel.auditModelId;
        [language] = auditModel.languages;
      }

      yield put(detailsActions.applicationReadySuccess(idToSelect, language));
    } catch (error) {
      yield put(detailsActions.applicationReadyFailure(error));
    }
  }
}

function* fetchAuditModel({ payload }) {
  const { request } = payload;
  try {
    const { data: auditModel } = yield call(restClient.request, request);
    yield put(detailsActions.fetchSuccess(auditModel));
  } catch (error) {
    yield put(detailsActions.fetchFailure(error));
  }
}

function* fetchFields({ payload }) {
  const { request } = payload;
  try {
    const { data: fields } = yield call(restClient.request, request);
    yield put(detailsActions.fetchFieldsSuccess(fields));
  } catch (error) {
    yield put(detailsActions.fetchFieldsFailure(error));
  }
}

function* fetchTranslations({ payload }) {
  const { request } = payload;
  const { langs, keys } = request.params;

  try {
    const { data: translations } = yield call(IntlService.getTranslationsForAllLanguages, langs, { keys });
    yield put(detailsActions.fetchTranslationsSuccess(translations));
  } catch (error) {
    yield put(detailsActions.fetchTranslationsFailure(error));
  }
}

function* create({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(detailsActions.createSuccess(data));
    // TODO - Change to avoid double fetch when creating + redirect to auditModels/{id} (see startApp and BMWVID-8977)
    yield call(navigateActions.navigateToAuditModelDetails, data.auditModelId);
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'audito.model.notification.create.success',
          defaultMessage: 'The audit model has been created with success',
        },
      })
    );
  } catch (error) {
    yield put(detailsActions.createFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.model.notification.create.error',
          defaultMessage: 'An error has occurred during the audit model creation.',
        },
      })
    );
  }
}

function* update({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(detailsActions.updateSuccess(data));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'audito.model.notification.update.success',
          defaultMessage: 'Audit template updated',
        },
      })
    );
  } catch (error) {
    yield put(detailsActions.updateFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.model.notification.update.error',
          defaultMessage: "Can't update the audit template",
        },
      })
    );
  }
}

function* duplicate({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(duplicateActions.duplicateSuccess(data));
    yield put(duplicateModalActions.hideModal());
    yield call(navigateActions.navigateToAuditModelDetails, data.auditModelId);
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'audito.page.models.notification.clone.success',
          defaultMessage: 'Audit template duplicated',
        },
      })
    );
  } catch (error) {
    yield put(duplicateActions.duplicateFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.page.models.notification.clone.error',
          defaultMessage: "Can't duplicate the audit template",
        },
      })
    );
  }
}

function* FetchDataWhenOpeningDuplicateModal({ payload }) {
  const { groupId } = payload;
  const params = {
    status: DEALER_STATUS.ACTIVE,
    groupIds: groupId,
    extensions: AUDIT,
    reducer: ['id,none', 'name,none', 'groupId,none'],
  };
  yield put(groupActions.fetchGroupsByExtension(AUDIT));
  yield put(dealerActions.fetchDealersByReducer(params));
}

function* fetchDealersOnGroupChange({ payload }) {
  const { groupId } = payload;
  if (groupId) {
    const params = {
      status: DEALER_STATUS.ACTIVE,
      groupIds: groupId,
      extensions: AUDIT,
      reducer: ['id,none', 'name,none', 'groupId,none'],
    };

    try {
      yield put(dealerActions.fetchDealersByReducer(params));
      /** @type {import('modules/dealers/types').GenericDealersActionPage} */
      const resultAction = yield take(allTypes.FETCH_DEALERS_SUCCESS);

      const { content: dealers } = resultAction.payload;
      // Because the backend does NOT sort the same way as the front
      const sortedDealers = dealers.sort((a, b) => a.name.localeCompare(b.name));
      const dealerId = sortedDealers[0].id;

      yield put(duplicateModalActions.setData({ dealerId }));
    } catch (error) {
      console.error('Error', error);
    }
  }
}

function* requestUnpublish({ payload: auditModelId }) {
  const auditModel = yield select(state => getById(state, auditModelId));
  const dealerIds = auditModel.activeDealers?.map(({ dealerId }) => dealerId) ?? [];

  if (dealerIds.length === 0) {
    yield put(dashboardActions.patchAuditModelStatus(auditModel.id, AUDIT_MODEL_STATUS.DRAFT));
    return;
  }

  yield put(dealerActions.fetchDealers(undefined, { ids: dealerIds, projection: 'page' }));
  yield take(allTypes.FETCH_DEALERS_SUCCESS);
  yield put(unpublishModalActions.displayUnpublishModal(auditModelId));
}

/**
 * @param {import('modules/auditModels/types').InitializeAuditModelSelectorAction} action
 */
function* initializeAuditModelSelect({ payload, meta }) {
  const params = yield select(state => auditModelsSelectSelector.getParams(state, payload.token));
  if (params.dealerId) {
    yield put(selectActions.getAuditModelsSelect(payload.token, params, meta));
  }
}

/**
 * @param {import('modules/auditModels/types').GetAuditModelsSelectAction} action
 */
function* fetchAuditModelsSelect({ payload, meta }) {
  const { request, token } = payload;
  try {
    const data = yield* fetchPage(request);
    yield put(selectActions.getAuditModelsSelectSuccess(token, data, meta));
  } catch (error) {
    yield put(selectActions.getAuditModelsSelectFailure(token, error, meta));
  }
}

export default function* auditModelsSagas() {
  yield fork(auditModelsDashboardSagas);
  yield takeLatest(actionTypes.FETCH, fetchAuditModel);
  yield takeLatest(actionTypes.FETCH_FIELDS, fetchFields);
  yield takeLatest(actionTypes.FETCH_TRANSLATIONS, fetchTranslations);
  yield takeLatest(actionTypes.CREATE, create);
  yield takeLatest(actionTypes.UPDATE, update);
  yield takeLatest(actionTypes.DUPLICATE, duplicate);
  yield takeLatest(actionTypes.DISPLAY_DUPLICATE_MODAL, FetchDataWhenOpeningDuplicateModal);
  yield takeLatest(actionTypes.SET_DUPLICATE_MODAL_DATA, fetchDealersOnGroupChange);
  yield takeLatest(actionTypes.UNPUBLISH, requestUnpublish);
  yield takeLatest(LOCATION_CHANGE, startDetailsApplication);
  yield takeEvery(actionTypes.GET_AUDIT_MODELS_SELECT_FETCH, fetchAuditModelsSelect);
  yield debounce(150, actionTypes.SET_AUDIT_MODELS_SELECT_FILTERS, initializeAuditModelSelect);
}
